import { useMemo, useState } from "react";
import ColorSwatch from "~/components/ColorSwatch";
import { ColorSelector } from "~/components/ProductColorSelector";
import useBreakpoints from "~/hooks/useBreakpoints";
import { formatCurrency } from "~/utils/number";

type Props = {
  colorSelector: ColorSelector;
  isActive: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  idModifier?: string;
  hasFlyout?: boolean;
};

const ColorRadioField: React.FC<Props> = ({
  colorSelector,
  isActive,
  onChange,
  hasFlyout = false,
  idModifier,
}) => {
  const { isMobile, isTablet } = useBreakpoints();
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);

  const colorSwatchColor = useMemo(() => {
    if (colorSelector.isDropship) {
      return "blue";
    }

    if (colorSelector.isCloseout) {
      return "red";
    }

    return undefined;
  }, [colorSelector.isCloseout, colorSelector.isDropship]);

  const roleDescription = useMemo(() => {
    if (colorSelector.isDropship) {
      return "dropship item";
    }

    if (colorSelector.isCloseout) {
      return "closeout item";
    }

    return "normal item";
  }, [colorSelector.isCloseout, colorSelector.isDropship]);

  return (
    <div
      aria-label={`${colorSelector.name} Color Selector`}
      onMouseEnter={() => hasFlyout && setIsFlyoutOpen(true)}
      onMouseLeave={() => hasFlyout && setIsFlyoutOpen(false)}
      className="w-[calc((100%/4)-(4*1rem)/4)] lg:w-[17px]"
    >
      <input
        type="radio"
        name="variant"
        className="sr-only"
        id={`colorSelector_${colorSelector.id}${idModifier ? `_${idModifier}` : ""}`}
        value={colorSelector.id}
        defaultChecked={isActive}
        aria-roledescription={roleDescription}
        onChange={onChange}
      />
      <label
        htmlFor={`colorSelector_${colorSelector.id}${idModifier ? `_${idModifier}` : ""}`}
        className="relative flex w-full flex-col items-center gap-1"
      >
        <ColorSwatch
          hexCodes={colorSelector.hexCodes}
          image={colorSelector.colorImage || null}
          isActive={isActive}
          outline={colorSwatchColor}
          size="100%"
        />
        <span
          className={`inline-block text-wrap text-center text-sm leading-4 text-black ${isTablet || isMobile ? "" : "sr-only"}`}
        >
          {colorSelector.name}
        </span>
        {hasFlyout && isFlyoutOpen ? (
          <div
            role="tooltip"
            className="absolute -left-[77px] bottom-[calc(100%+16px)] z-[1000] flex w-[170px] flex-col rounded-[6px] bg-white shadow-[0_0_7px_5px_rgba(50,50,50,0.09)]"
          >
            <h5 className="w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-t-[6px] bg-blue100 text-center text-sm uppercase text-black">
              {colorSelector.name}
            </h5>
            <div className="w-full">
              <img
                src={
                  colorSelector.productImage ??
                  `/frontend-assets/images/placeholder-tshirt.png`
                }
                alt={`${colorSelector.name} flyout image`}
                className="block h-auto w-full"
              />
            </div>
            <span className="tooltip__price after:border-l-transparent after:border-r-transparent relative w-full rounded-b-[6px] bg-[#004c79] text-center text-sm uppercase text-white after:absolute after:bottom-[-12px] after:left-[calc(50%-10px)] after:block after:h-0 after:w-0 after:border-l-[10px] after:border-r-[10px] after:border-t-[12px] after:border-t-[#004c79] after:content-['']">
              Starts at: {formatCurrency(colorSelector.startingPrice)}
            </span>
          </div>
        ) : null}
      </label>
    </div>
  );
};

export default ColorRadioField;
